import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { LocationFacade } from '@app/modules/location/facade/location.facade';
import { Subject } from 'rxjs';
import { filter, takeUntil, withLatestFrom, tap } from 'rxjs/operators';
import { DetailsSubcontext, ViewContext } from '@app/store/layout/reducers/layout.reducer';
import { MobileLayoutService } from '@app/services/mobile-layout.service';
import { EntityResource } from '@app/store/filters/models/filters.model';
import { Orientation } from '@app/modules/platform/store/reducers/platform.reducer';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { AssetDetailsFacade } from '@app/modules/asset-details-live-container/facade/asset-details-facade.service';
import { LoadingAnimationService } from '@app/services/loading-animation.service';
import { Translations } from '@app/core/services/i18n/translations.service';
import { TranslateService } from '@livemaps/i18n';

@Component({
  selector: 'app-mobile-location-container',
  templateUrl: './mobile-location-container.component.html',
  styleUrls: ['./mobile-location-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class MobileLocationContainerComponent implements OnInit, OnDestroy {
  @Output() handleOpenFilterDialog: EventEmitter<any> = new EventEmitter<any>();

  onDestroy$ = new Subject<void>();
  filterChipValues = new Array<EntityResource>();
  isAssetSelected = false;
  currentView = ViewContext.MOBILE_MAP;
  views = ViewContext;
  showRotationMessage: boolean;
  assetsLoadState = this.locationFacade.getAssetsLoadState();
  companiesLoadState = this.locationFacade.getCompaniesLoadState();
  recentPathLoadState = this.locationFacade.getRecentPathLoadState();
  viewSubContext = this.locationFacade.getSelectedViewSubContext();
  liveValue = DetailsSubcontext.LIVE;
  historyValue = DetailsSubcontext.HISTORY;
  translated;
  text;
  contextSelectorText = {
    [ViewContext.LIST]: '',
    [ViewContext.MOBILE_MAP]: ''
  };
  loading = true;
  constructor(
    public locationFacade: LocationFacade,
    private mobileLayoutService: MobileLayoutService,
    private changeDetectorRef: ChangeDetectorRef,
    public assetDetailsFacade: AssetDetailsFacade,
    public loadingAnimationService: LoadingAnimationService,
    public translations: Translations,
    public translateService: TranslateService
  ) {
    this.translations.translationsLoadState
      .pipe(filter(loadstate => loadstate === ResourceLoadState.LOAD_SUCCESSFUL))
      .subscribe(() => {
        this.translated = this.translateService.instant([
          this.translations.selectortextmobile.viewMap,
          this.translations.selectortextmobile.viewList
        ]);
        this.contextSelectorText = {
          [ViewContext.LIST]: this.translated[this.translations.selectortextmobile.viewMap],
          [ViewContext.MOBILE_MAP]: this.translated[this.translations.selectortextmobile.viewList]
        };
        this.text = this.contextSelectorText[this.currentView];
      });
  }

  hideMobileSidenavHeader: HTMLElement;
  ngOnInit() {
    // need to clear selectd asset again in case user clicks back to list view via browser back button
    this.assetDetailsFacade.clearSelectedAsset();
    this.locationFacade.setViewContext(ViewContext.MOBILE_MAP);
    this.getAreAssetsLoading();

    this.locationFacade
      .getOrientation()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(orientation => {
        this.showRotationMessage = orientation === Orientation.LANDSCAPE;
        this.changeDetectorRef.detectChanges();
      });

    // get filter chips with this workflow for limited user
    this.locationFacade
      .getFilterChipLabels()
      .pipe(
        withLatestFrom(this.locationFacade.getReadCompaniesPerm()),
        // explicit "false" check more desirable because there's a brief
        // period of time where readCompanies is undefined
        // while we wait for auth to complete
        filter(([chips, readCompanies]) => readCompanies === false),
        // the boolean returning from getShowFilterChipsForLimitedUser
        // will have been previously set to true when a limited user clicks the "Done" button on the filter dialog
        // it is false by default to avoid initial loading of chips on page load
        // it gets reset to false when user clicks Clear btn
        withLatestFrom(this.locationFacade.getShowFilterChipsForLimitedUser()),
        takeUntil(this.onDestroy$)
      )
      .subscribe(([[chips, readCompanies], showChips]) => {
        this.filterChipValues = showChips ? chips : [];
        this.changeDetectorRef.detectChanges();
      });

    // get filter chips with this workflow for Zonar user
    this.locationFacade
      .getFilterChipLabels()
      .pipe(
        withLatestFrom(this.locationFacade.getReadCompaniesPerm()),
        filter(([chips, readCompanies]) => readCompanies),
        takeUntil(this.onDestroy$)
      )
      .subscribe(([chips, perms]) => {
        this.filterChipValues = chips;
        this.changeDetectorRef.detectChanges();
      });

    // show/hide sidenav-header based on VDO native app CNAME
    if (window.location.hostname.includes('vdo-native-app')) {
      this.hideMobileSidenavHeader = document.getElementById('sidenav-header');
      if (this.hideMobileSidenavHeader) {
        this.hideMobileSidenavHeader.style.display = 'block';
      }
    }
  }

  getAreAssetsLoading() {
    if (this.loadingAnimationService.shouldShowAssetsLoadingAnimations) {
      this.locationFacade
        .getAssetsLoadState()
        .pipe(
          tap(loadState => {
            this.loading = loadState === ResourceLoadState.LOADING || loadState === ResourceLoadState.INITIAL;
            this.changeDetectorRef.detectChanges();
          }),
          takeUntil(this.onDestroy$)
        )
        .subscribe();
    } else {
      this.loading = false;
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getChipValues(): EntityResource[] {
    return this.isAssetSelected ? [] : this.filterChipValues;
  }

  handleFilter() {
    this.handleOpenFilterDialog.emit(true);
  }

  handleChipRemoved(chip) {
    this.locationFacade.removeFilter(chip);
  }

  handleChipsHeight(height) {
    this.mobileLayoutService.setChipsHeight(height);
  }

  loadingInProgress(companiesLoadState, assetsLoadState, recentPathLoadState) {
    const okCompanies =
      companiesLoadState === ResourceLoadState.INITIAL || companiesLoadState === ResourceLoadState.LOADING;

    const okAssets = assetsLoadState === ResourceLoadState.LOADING;

    const okRecentPath = recentPathLoadState === ResourceLoadState.LOADING;

    return okCompanies || okAssets || okRecentPath;
  }

  setView() {
    if (this.currentView === this.views.LIST) {
      this.currentView = this.views.MOBILE_MAP;
      // if we are in list view, we set the map pane to height 0 so that it can be reszied properly to have copyright logo in bottom right corner
      return;
    }
    this.currentView = this.views.LIST;
  }

  setText() {
    if (this.text === this.contextSelectorText[ViewContext.MOBILE_MAP]) {
      this.text = this.contextSelectorText[ViewContext.LIST];
      return;
    }
    this.text = this.contextSelectorText[ViewContext.MOBILE_MAP];
  }

  toggleMobileViewContext() {
    this.setView();
    this.setText();
  }
}
